import React, { useEffect, useRef, useState } from "react";
import { httpPost, useHttpGets } from "../../../../services/httpService";
import { ApiEndPoints } from "../../../../utils/endpoints";
import { MEMBER_KEY, Messages } from "../../../../utils/constants";
import {
  REIButton,
  REIDialoge,
  REIDivider,
  REIInputText,
  REIProgressSpinner,
  REIToastContainer,
} from "../../../../components/ui";
import AssignEntity from "../../PropertyStepper/Child/TaxAndMarket/AssignEntity";
import { useAnalysedPropertyContext } from "../../PropertyStepper/Child/AnalysedPropertyContext";
import {
  IAnalysedProperty,
  IGrowth,
  IPropertyAnnualExpense,
  IPropertyAnnualRentalReturns,
  IPropertyDepreciationEstimation,
  IPropertyTaxRate,
  ILoan,
} from "../../../../interface/addAnalyser";
import { IMemberEntity, IPropertyMarketFactor } from "../../../../interface/analysed";
import { getAdjustedFormattedDate, getDateStringWithGivenFormat } from "../../../../utils/commonUtil";
import LoanDetailsTab from "../../PropertyStepper/Child/LoanDetailsTab";
import { useNavigate } from "react-router-dom";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { IAddPropertyToPortfolioRequest, ILease, IPropertyTrackerData } from "../../../../interface/portfolioTracker";

const MoveToPortfolioTracker: React.FC<{
  propertyAnalyserId: number;
  memberCountryCode: string;
  memberEntity: IMemberEntity[];
  propertyName?: string;
}> = ({ propertyAnalyserId, memberCountryCode, memberEntity, propertyName }) => {
  const [PropertyTrackerData, setPropertyTrackerData] = useState<IPropertyTrackerData>();
  const [addPropertyToPortfolioRequest, setAddPropertyToPortfolioRequest] = useState<IAddPropertyToPortfolioRequest>();
  const [lease, setLease] = useState<ILease>({
    Tenants: "",
    ReviewDate: "",
    EndDate: "",
    StartDate: "",
    ReviewAlert: "",
  });
  const PortfolioRequestData = useRef<IAddPropertyToPortfolioRequest>();
  const { setAnalysedProperty, analysedProperty } = useAnalysedPropertyContext();
  const [loanDetails, setLoanDetails] = useState<ILoan[]>([]);
  const [isPorfolioSuccess, setIsPorfolioSuccess] = useState<boolean>(false);
  const [tanents, setTanents] = useState<string>("");
  const [purchaseDate, setPurchaseDate] = useState<string>(getDateStringWithGivenFormat(new Date(Date.now())));
  const portfolioTrackerId = useRef<number>(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const toastRef = useRef<Toast>(null);
  const loanRefs = useRef<(React.ElementRef<typeof LoanDetailsTab> | null)[]>([]);
  const [dateErrors, setDateErrors] = useState({
    purchaseDate: false,
    loanDate: false,
    leaseStartDate: false,
    leaseEndDate: false,
    rentReviewDate: false,
    nextReviewDate: false,
  });

  useEffect(() => {
    const getData = () => {
      useHttpGets(
        `${ApiEndPoints.analyser}/PropertyAnalyserToTracker?MemberId=${localStorage.getItem(
          MEMBER_KEY
        )}&PropertyAnalyserId=${propertyAnalyserId}&MemberCountryCode=${memberCountryCode}`,
        undefined
      ).then((result: { data: IPropertyTrackerData | null; error: string | null; status: number | null }) => {
        if (result.status == 200) {
          const defaultLoan: ILoan = {
            LoanId: 0,
            Amount: 295200,
            Date: getDateStringWithGivenFormat(new Date(Date.now())),
            EstablishmentFees: 0,
            FixedInterestPeriod_Months: 6,
            FixedInterestPeriod_Years: 1,
            InterestRate: 5,
            InterestRateReviewDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 3 }),
            Length_Months: 0,
            Length_Years: 0,
            Name: "",
            OtherLoanCosts: 0,
            ValuationFees: 0,
            IsPrincipal: 0,
            isFromPortfolio: true,
            Balance: 0,
            PropertyAnalyserid: 0,
          };

          const loans =
            result.data?.Loans && result.data.Loans.length > 0
              ? result.data.Loans.map((loan: ILoan) => ({
                  ...loan,
                  FixedInterestPeriod_Years: loan.FixedInterestPeriod_Years || 6,
                  FixedInterestPeriod_Months: loan.FixedInterestPeriod_Months || 1,
                  InterestRateReviewDate:
                    loan.InterestRateReviewDate || getAdjustedFormattedDate(new Date(Date.now()), { years: 3 }),
                  Date: loan.Date !== undefined ? loan.Date : getDateStringWithGivenFormat(new Date(Date.now())),
                  isFromPortfolio: true,
                  Balance: loan.Amount != undefined ? loan.Amount : 0,
                }))
              : [defaultLoan];
          setPropertyTrackerData({
            ...result.data,
            Loans: loans,
          });

          setTrackerDataToContext(result.data);
          setLoanDetails(loans);
          initializeAddPropertyToPortfolioRequest();
          setLease({
            EndDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 1 }),
            ReviewAlert: "0",
            ReviewDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 1 }),
            StartDate: getDateStringWithGivenFormat(new Date(Date.now())),
            Tenants: "",
          });
        } else {
          return <REIProgressSpinner></REIProgressSpinner>;
        }
      });
    };
    if (propertyAnalyserId) {
      getData();
    }
  }, [propertyAnalyserId]);

  useEffect(() => {
    if (PropertyTrackerData?.Loans && PropertyTrackerData?.Loans.length > 0) {
      handleUpdateProperty({
        LoanTypeCost: {
          ...analysedProperty?.LoanTypeCost,
          Loans: loanDetails,
          PropertyOtherPurchaseExpense: analysedProperty?.LoanTypeCost.PropertyOtherPurchaseExpense || {
            CapitalInvestment: 0,
            OtherCost: 0,
            SolicitorsCost: 0,
            StampDuty: 0,
          },
          PropertyPurchaseCost: analysedProperty?.LoanTypeCost.PropertyPurchaseCost || {
            CashDeposit: 0,
            MarketValueNow: 0,
            PurchaseAmount: 0,
            EquityDeposit: 0,
            isEnterPercentage: "0",
            isEnterValue: "0",
          },
        },
      });
    }
  }, [loanDetails]);

  useEffect(() => {
    if (purchaseDate && loanDetails?.length > 0) {
      const purchaseDateObj = new Date(purchaseDate);
      const loanDateObj = new Date(loanDetails[0].Date);
      const reviewDateObj = new Date(loanDetails[0].InterestRateReviewDate);
      const today = new Date();

      if (loanDetails?.[0]?.Date) {
        setDateErrors((prev) => ({
          ...prev,
          loanDate: loanDateObj < purchaseDateObj || loanDateObj > today,
        }));
      }

      if (loanDetails?.[0]?.InterestRateReviewDate) {
        setDateErrors((prev) => ({
          ...prev,
          nextReviewDate: reviewDateObj <= loanDateObj,
        }));
      }
      if (lease?.StartDate) {
        const leaseStartDateObj = new Date(lease.StartDate);
        setDateErrors((prev) => ({
          ...prev,
          leaseStartDate: leaseStartDateObj < purchaseDateObj,
        }));
      }
    }
  }, [purchaseDate, loanDetails, lease?.StartDate]);

  useEffect(() => {
    if (lease?.StartDate && lease?.ReviewDate) {
      const leaseStartDateObj = new Date(lease.StartDate);
      const reviewDateObj = new Date(lease.ReviewDate);
      const leaseEndDateObj = new Date(lease.EndDate);
      setDateErrors((prev) => ({
        ...prev,
        rentReviewDate: reviewDateObj <= leaseStartDateObj,
        leaseEndDate: leaseEndDateObj <= leaseStartDateObj,
      }));
    }
  }, [lease?.StartDate]);

  useEffect(() => {
    if (lease?.StartDate && lease?.EndDate) {
      const startDateObj = new Date(lease.StartDate);
      const endDateObj = new Date(lease.EndDate);
      setDateErrors((prev) => ({
        ...prev,
        leaseEndDate: endDateObj <= startDateObj,
      }));
    }
  }, [lease?.StartDate, lease?.EndDate]);

  const handleUpdateProperty = (updatedProperty: Partial<IAnalysedProperty>) => {
    setAnalysedProperty((prevState) => {
      if (!prevState) return undefined;
      return {
        ...prevState,
        ...updatedProperty,
      };
    });
  };

  const initializeAddPropertyToPortfolioRequest = () => {
    const initialRequest: IAddPropertyToPortfolioRequest = {
      PropertyAnalyserId: propertyAnalyserId,
      MemberEntityId: Number(PropertyTrackerData?.MemberEntityId) || 0,
      PurchasePrice: PropertyTrackerData?.PurchasePrice || 0,
      Loans: [
        {
          AnalyserLoanId: 0,
          Balance: 0,
          Date: getDateStringWithGivenFormat(new Date(Date.now()), "dd/mm/yy"),
          EstablishmentFees: 0,
          FixedInterestPeriod_Months: 1,
          FixedInterestPeriod_Years: 1,
          InterestRate: 0,
          InterestRateReviewAlert: "",
          InterestRateReviewDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 3 }),
          Length_Months: 1,
          Length_Years: 1,
          Name: "",
          OtherLoanCost: 0,
          RefCode: "",
          ValuationFees: 0,
          isNewLoan: 1,
          isPrincipal: "1",
        },
      ],
      Lease: {
        EndDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 1 }),
        ReviewAlert: "",
        ReviewDate: getAdjustedFormattedDate(new Date(Date.now()), { years: 1 }),
        StartDate: getDateStringWithGivenFormat(new Date()),
        Tenants: "",
      },
      PortionedOwned: 0,
      MemberId: localStorage.getItem(MEMBER_KEY) || "",
      PurchaseDate: getDateStringWithGivenFormat(new Date()),
      CountryCode: "",
      userCountryCode: "",
      Year1ProRata: 0,
      Year1ProRataDateExpense: null,
      Year1ProRataDateIncome: null,
      DepreciationValues: {
        VIConstructionYear_DepreciationValue: "",
        VIEstimatedLandValue_DepreciationValue: "0",
        VIRenovation_DepreciationValue: "",
        WBConstructionYear_DepreciationValue: "",
        WBNearestCapitalCity_DepreciationValue: "",
        WBPropertyType_DepreciationValue: "",
        WBQuality_DepreciationValue: "",
      },
      CalculateDepreciation: 0,
    };
    setAddPropertyToPortfolioRequest(initialRequest);
    PortfolioRequestData.current = initialRequest;
  };

  const moveToPortfolioTracker = async () => {
    if (!validateDates()) {
      toastRef.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please correct the date errors before proceeding",
        life: 3000,
      });
      return;
    }

    const allLoansValid = loanRefs.current.filter((ref) => ref !== null).every((ref) => ref.validate());
    if (!allLoansValid) {
      toastRef.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill in all required loan fields",
        life: 3000,
      });
      return;
    }

    mapAnalyserDataToPortfolioRequest();
    setLoading(true);
    await httpPost(`${ApiEndPoints.tracker}PortfolioTracker/FromAnalyser/`, PortfolioRequestData.current, null)
      .then((result) => {
        if (result.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: Messages.SUCCESS,
            life: 2000,
          });
          setIsPorfolioSuccess(true);
          setLoading(false);
          portfolioTrackerId.current = result.data;
        }
      })
      .catch((error) => {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
        setLoading(false);
      });
  };

  const setTrackerDataToContext = (data: IPropertyTrackerData) => {
    const initialProperty: IAnalysedProperty = {
      LoanTypeCost: {
        Loans: data.Loans.map((loan: ILoan) => ({
          ...loan,
          FixedInterestPeriod_Years: loan.FixedInterestPeriod_Years || 0,
          FixedInterestPeriod_Months: loan.FixedInterestPeriod_Months || 0,
          InterestRateReviewDate: loan.InterestRateReviewDate || "",
          Date: loan.Date || "",
          isFromPortfolio: true,
        })),
        PropertyOtherPurchaseExpense: {
          CapitalInvestment: 0,
          OtherCost: 0,
          SolicitorsCost: 100,
          StampDuty: 0,
        },
        PropertyPurchaseCost: {
          CashDeposit: 10,
          EquityDeposit: 0,
          MarketValueNow: 0,
          PurchaseAmount: data.PurchasePrice,
          isEnterPercentage: "1",
          isEnterValue: "0",
        },
      },
      PropertyDetail: {
        BathroomId: 0,
        BedroomId: 0,
        CarportId: 0,
        CountryId: 0,
        DateAdded: new Date().toLocaleDateString("en-GB"),
        Landsize: "0",
        MemberPropertyId: "0",
        ModulesId: "0",
        PropertyDetailId: 0,
        PropertyName: "",
        PropertyTypeId: 0,
        UserCountryId: 0,
        district: "",
        locality: "",
        postcode: "",
        state: "",
        useDefault: "0",
        Growth: {
          value: 0,
          description: "",
          PropertyType: "",
          StatsPostcode: "",
          StatsPostcodeLongTermGrowth: 0,
          StatsRegion: "",
          StatsRegionLongTermGrowth: 0,
          StatsSuburb: "",
          StatsSuburbLongTermGrowth: 0,
        } as IGrowth,
        MemberEntityId: data.MemberEntityId.toString(),
        MemberProfiles: [],
        PortionOwned: data.PortionOwned.toString(),
      },
      RentExpense: {
        PropertyAnnualExpense: {} as IPropertyAnnualExpense,
        PropertyAnnualRentalReturns: {} as IPropertyAnnualRentalReturns,
      },
      TaxMarket: {
        PropertyDepreciationEstimation: {} as IPropertyDepreciationEstimation,
        PropertyMarketFactor: {} as IPropertyMarketFactor,
        PropertyTaxRate: {} as IPropertyTaxRate,
      },
    };
    PortfolioRequestData.current = initialProperty;
    setAnalysedProperty(initialProperty);
  };

  const getFormattedDate = (date: string, format: "dd/mm/yy" | "mm/dd/yy" | "yyyy/mm/dd" = "mm/dd/yy"): string => {
    const parsedDate = new Date(date);

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(1, "0");
    const year = String(parsedDate.getFullYear());

    return `${day}/${month}/${year}`;
  };

  const mapAnalyserDataToPortfolioRequest = () => {
    const data: IAddPropertyToPortfolioRequest = {
      PropertyAnalyserId: Number(propertyAnalyserId),
      MemberEntityId: Number(analysedProperty?.PropertyDetail.MemberEntityId) || 0,
      PurchasePrice: PropertyTrackerData?.PurchasePrice,
      Loans: loanDetails
        ? loanDetails.map((loan) => ({
            AnalyserLoanId: loan.LoanId,
            Balance: loan.Amount != undefined ? loan.Amount : 0,
            Date: getFormattedDate(loan.Date, "dd/mm/yy"),
            EstablishmentFees: loan.EstablishmentFees,
            FixedInterestPeriod_Months: loan.FixedInterestPeriod_Months,
            FixedInterestPeriod_Years: loan.FixedInterestPeriod_Years,
            InterestRate: loan.InterestRate,
            InterestRateReviewAlert: 0,
            InterestRateReviewDate: getFormattedDate(loan.InterestRateReviewDate, "dd/mm/yy"),
            Length_Months: loan.Length_Months,
            Length_Years: loan.Length_Years,
            Name: loan.Name,
            OtherLoanCost: loan.OtherLoanCosts,
            RefCode: loan.RefCode || "",
            ValuationFees: loan.ValuationFees,
            isNewLoan: 1,
            isPrincipal: loan.IsPrincipal.toString(),
          }))
        : [],
      Lease_Tenants: tanents != undefined ? tanents : "",
      Lease_EndDate:
        lease?.EndDate != undefined
          ? getFormattedDate(lease?.EndDate, "dd/mm/yy")
          : getAdjustedFormattedDate(new Date(Date.now()), { years: 1 }),
      Lease_ReviewAlert: 0,
      Lease_ReviewDate:
        lease?.ReviewDate != undefined
          ? getFormattedDate(lease?.ReviewDate, "dd/mm/yy")
          : getAdjustedFormattedDate(new Date(Date.now()), { years: 1 }),
      Lease_StartDate:
        lease?.StartDate != undefined
          ? getFormattedDate(lease?.StartDate, "dd/mm/yy")
          : getDateStringWithGivenFormat(new Date()),
      PortionedOwned: Number(analysedProperty?.PropertyDetail.PortionOwned),
      MemberId: localStorage.getItem(MEMBER_KEY),
      PurchaseDate: getFormattedDate(purchaseDate, "dd/mm/yy"),
      CountryCode: memberCountryCode,
      userCountryCode: memberCountryCode,
      CalculateDrepreciation: 1,
      VIConstructionYear_DepreciationValue:
        PropertyTrackerData?.PropertyDepreciationDepreciationValues.VIConstructionYear_DepreciationValue,
      VIEstimatedLandValue_DepreciationValue:
        PropertyTrackerData?.PropertyDepreciationDepreciationValues.VIEstimatedLandValue_DepreciationValue,
      VIRenovation_DepreciationValue:
        PropertyTrackerData?.PropertyDepreciationDepreciationValues.VIRenovation_DepreciationValue,
      WBConstructionYear_DepreciationValue:
        PropertyTrackerData?.PropertyDepreciationDepreciationValues.WBConstructionYear_DepreciationValue,
      WBNearestCapitalCity_DepreciationValue:
        PropertyTrackerData?.PropertyDepreciationDepreciationValues.WBNearestCapitalCity_DepreciationValue,
      WBPropertyType_DepreciationValue:
        PropertyTrackerData?.PropertyDepreciationDepreciationValues.WBPropertyType_DepreciationValue,
      WBQuality_DepreciationValue:
        PropertyTrackerData?.PropertyDepreciationDepreciationValues.WBQuality_DepreciationValue,
      Year1ProRata: 0,
      Year1ProRataDateExpense: null,
      Year1ProRataDateIncome: null,
    };
    setAddPropertyToPortfolioRequest((prevState) => {
      return { ...prevState, ...data };
    });
    PortfolioRequestData.current = data;
  };

  const setValues = (name: string, value: string | number) => {
    setAddPropertyToPortfolioRequest((prevState) => {
      if (!prevState) return undefined;
      const updatedExpense = {
        ...prevState,
        [name]: value !== undefined ? value : 0,
      };
      return updatedExpense;
    });
  };

  const setLeaseData = (name: string, value: string | number) => {
    setLease((prevState) => {
      if (!prevState) return undefined;
      const updatedLease = {
        ...prevState,
        [name]: value !== undefined ? value : 0,
      };
      return updatedLease;
    });
  };

  const updateLoanValue = (loan: ILoan) => {
    if (loanDetails) {
      const loans = loanDetails.map((l) => (l.LoanId === loan.LoanId ? loan : l));
      setLoanDetails(loans);
    }
  };

  const validateDates = (): boolean => {
    const today = new Date();
    const purchaseDateObj = new Date(purchaseDate);
    const leaseStartDateObj = lease?.StartDate ? new Date(lease.StartDate) : null;
    const leaseEndDateObj = lease?.EndDate ? new Date(lease.EndDate) : null;
    const rentReviewDateObj = lease?.ReviewDate ? new Date(lease.ReviewDate) : null;
    const reviewDateObj = new Date(loanDetails[0].InterestRateReviewDate);
    const loanDateObj = new Date(loanDetails[0].Date);

    let isValid = true;
    const newErrors = {
      purchaseDate: false,
      loanDate: false,
      leaseStartDate: false,
      leaseEndDate: false,
      rentReviewDate: false,
      nextReviewDate: false,
    };

    if (purchaseDateObj > today) {
      newErrors.purchaseDate = true;
      isValid = false;
    }

    if (loanDateObj) {
      if (loanDateObj < purchaseDateObj || loanDateObj > today) {
        newErrors.loanDate = true;
        isValid = false;
      }
    }

    if (leaseStartDateObj && leaseStartDateObj < purchaseDateObj) {
      newErrors.leaseStartDate = true;
      isValid = false;
    }

    if (leaseStartDateObj && leaseEndDateObj && leaseEndDateObj <= leaseStartDateObj) {
      newErrors.leaseEndDate = true;
      isValid = false;
    }

    if (rentReviewDateObj && leaseStartDateObj && rentReviewDateObj <= leaseStartDateObj) {
      newErrors.rentReviewDate = true;
      isValid = false;
    }

    if (reviewDateObj <= loanDateObj) {
      newErrors.nextReviewDate = true;
      isValid = false;
    }

    setDateErrors(() => newErrors);
    return isValid;
  };

  return (
    <>
      <REIToastContainer toastReference={toastRef} />
      {loading && <REIProgressSpinner />}
      <REIDialoge
        contentComponent={
          <>
            <h3>The property has been successfully added to your Portfolio Tracker portfolio</h3>
            <div>{propertyName}</div>
            <div className="flex row mt-1">
              <REIButton
                id="mptt-btn-ok"
                label="OK"
                disabled={false}
                icon=""
                classNames=""
                onClick={() => {
                  navigate(`/portfolio-tracker`, {
                    state: {
                      tab: "MyPropertyTab",
                      MyPropertyId: portfolioTrackerId.current,
                    },
                  });
                }}
              />
            </div>
          </>
        }
        handleEditClick={() => {
          setIsPorfolioSuccess(false);
        }}
        header="Property added to Portfolio Tracker"
        showHeader={true}
        visible={isPorfolioSuccess}
        customClass="w-3/4"
      ></REIDialoge>
      <div className="gap-3">
        <div className="w-full">
          <h3>{propertyName}</h3>
        </div>
        <REIDivider />
        <div className="w-full mb-5">
          You are about to save this property to your Portfolio Tracker. This is also your chance to assign it to a
          particular entity if you have several, and if you don't then simply leave as is and select 'save'.
        </div>
        <div className="w-full">
          <AssignEntity
            memberEntities={memberEntity}
            setIsValid={() => {}}
            updateProperty={handleUpdateProperty}
          ></AssignEntity>
        </div>
        <div className="relative rounded-lg p-2 border-solid border border-gray-300 bg-white mt-2">
          <div className="flex w-full items-center p-1 gap-2">
            <div className="w-[20%]">Purchase date</div>
            <div className="w-[80%] flex">
              <div className="flex flex-col gap-1">
                <Calendar
                  dateFormat="dd/mm/yy"
                  value={
                    purchaseDate && !isNaN(new Date(purchaseDate).getTime())
                      ? new Date(purchaseDate)
                      : new Date(Date.now())
                  }
                  onChange={(e) => {
                    if (e.value) {
                      const selectedDate = e.value as Date;
                      const dateValue = getDateStringWithGivenFormat(selectedDate);

                      setPurchaseDate(dateValue);

                      const today = new Date();
                      const isInvalid = selectedDate > today;

                      setDateErrors((prev) => ({
                        ...prev,
                        purchaseDate: isInvalid,
                      }));
                    }
                  }}
                  showIcon={true}
                  mask="99/99/9999"
                  className={dateErrors.purchaseDate ? "p-invalid" : ""}
                  readOnlyInput={false}
                  showOnFocus={false}
                  hideOnDateTimeSelect={true}
                ></Calendar>
                {dateErrors.purchaseDate && (
                  <small className="text-red-500">Purchase date cannot be in the future</small>
                )}
              </div>
            </div>
          </div>
        </div>
        <div>
          {loanDetails &&
            loanDetails.map((loan, index) => {
              return (
                <div
                  className="relative rounded-lg pl-3 pr-1 py-3 border-solid border border-gray-300 bg-white mt-2"
                  key={loan.LoanId}
                >
                  <div>
                    <h3>Loan {index + 1}</h3>
                  </div>
                  <div>
                    <LoanDetailsTab
                      ref={(el) => (loanRefs.current[index] = el)}
                      key={loan.LoanId}
                      loan={loan}
                      updateLoanValue={updateLoanValue}
                      movetoPortfolio={true}
                      purchaseDate={purchaseDate}
                      dateErrors={{ loanDate: dateErrors.loanDate, nextReviewDate: dateErrors.nextReviewDate }} // Pass error state
                      onDateValidation={(type, isValid) => {
                        setDateErrors((prev) => ({
                          ...prev,
                          [type]: !isValid,
                        }));
                      }}
                    ></LoanDetailsTab>
                  </div>
                </div>
              );
            })}
        </div>
        <div className="relative rounded-lg p-5 border-solid border border-gray-300 bg-white mt-5">
          <div className="grid grid-cols-12 gap-4 items-center mb-4">
            <div className="col-span-2 flex items-center">
              <label>Tenants</label>
            </div>
            <div className="col-span-4">
              <REIInputText
                id="mptt-txt-tenants"
                name="tanents"
                type="text"
                onChange={(e) => {
                  setValues("Tenants", e.target.value);
                  setTanents(e.target.value);
                }}
                value={tanents}
                className="w-full"
              />
            </div>

            <div className="col-span-2 flex items-center">
              <label>Rent Review</label>
            </div>
            <div className="col-span-4">
              <Calendar
                value={
                  lease?.ReviewDate && !isNaN(new Date(lease.ReviewDate).getTime())
                    ? new Date(lease.ReviewDate)
                    : new Date(Date.now())
                }
                onChange={(e) => {
                  if (e.value) {
                    const selectedDate = e.value as Date;
                    const dateValue = getDateStringWithGivenFormat(selectedDate);
                    setLeaseData("ReviewDate", dateValue);

                    const leaseStartDateObj = lease?.StartDate ? new Date(lease.StartDate) : new Date();
                    const isInvalid = selectedDate <= leaseStartDateObj;

                    setDateErrors((prev) => ({
                      ...prev,
                      rentReviewDate: isInvalid,
                    }));
                  }
                }}
                dateFormat="dd/mm/yy"
                showIcon={true}
                className={`w-full ${dateErrors.rentReviewDate ? "p-invalid" : ""}`}
                mask="99/99/9999"
                readOnlyInput={false}
                showOnFocus={false}
                hideOnDateTimeSelect={true}
              />
              <div className="h-5 overflow-hidden">
                {dateErrors.rentReviewDate && (
                  <small className="text-red-500">Review date must be after lease start date</small>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-2 flex items-center">
              <label>Lease Start Date</label>
            </div>
            <div className="col-span-4">
              <Calendar
                value={
                  lease?.StartDate && !isNaN(new Date(lease.StartDate).getTime())
                    ? new Date(lease.StartDate)
                    : new Date(Date.now())
                }
                onChange={(e) => {
                  if (e.value) {
                    const selectedDate = e.value as Date;
                    const dateValue = getDateStringWithGivenFormat(selectedDate);
                    setLeaseData("StartDate", dateValue);

                    const purchaseDateObj = new Date(purchaseDate);
                    const isInvalid = selectedDate < purchaseDateObj;

                    setDateErrors((prev) => ({
                      ...prev,
                      leaseStartDate: isInvalid,
                    }));
                  }
                }}
                dateFormat="dd/mm/yy"
                showIcon={true}
                className={`w-full ${dateErrors.leaseStartDate ? "p-invalid" : ""}`}
                mask="99/99/9999"
                readOnlyInput={false}
                showOnFocus={false}
                hideOnDateTimeSelect={true}
              />
              <div className="h-5 overflow-hidden" hidden={!dateErrors.leaseStartDate}>
                <small className="text-red-500">Lease start date must be after or equal to purchase date</small>
              </div>
            </div>

            <div className="col-span-2 flex items-center">
              <label>Lease End Date</label>
            </div>
            <div className="col-span-4">
              <Calendar
                value={
                  lease?.EndDate && !isNaN(new Date(lease.EndDate).getTime())
                    ? new Date(lease.EndDate)
                    : new Date(Date.now())
                }
                onChange={(e) => {
                  if (e.value) {
                    const selectedDate = e.value as Date;
                    const dateValue = getDateStringWithGivenFormat(selectedDate);
                    setLeaseData("EndDate", dateValue);

                    const leaseStartDateObj = lease?.StartDate ? new Date(lease.StartDate) : new Date();
                    const isInvalid = selectedDate <= leaseStartDateObj;

                    setDateErrors((prev) => ({
                      ...prev,
                      leaseEndDate: isInvalid,
                    }));
                  }
                }}
                dateFormat="dd/mm/yy"
                showIcon={true}
                className={`w-full ${dateErrors.leaseEndDate ? "p-invalid" : ""}`}
                mask="99/99/9999"
                readOnlyInput={false}
                showOnFocus={false}
                hideOnDateTimeSelect={true}
              />
              <div className="h-5 overflow-hidden" hidden={!dateErrors.leaseEndDate}>
                <small className="text-red-500">Lease end date must be after lease start date</small>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end w-full mt-5">
          <REIButton
            id="mptt-btn-next"
            onClick={() => {
              moveToPortfolioTracker();
            }}
            label="Next"
            classNames="px-6 py-2 text-white rounded-md"
          />
        </div>
      </div>
    </>
  );
};

export default MoveToPortfolioTracker;
